/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { ReactNode } from 'react';

import { Radio, TagLabel } from '@zapier/design-system';

import { Border, Colors } from '@zapier/checkout/src/constants';

import { createKeyHandler } from './createKeyHandler';

type Props = {
  enticement?: string;
  isCompact?: boolean;
  isDisabled?: boolean;
  isMetered?: boolean;
  isOnly?: boolean;
  isSelected?: boolean;
  name: string;
  onClick: () => void;
  price: ReactNode;
};

const billingOptionCss = (props: Props) =>
  css([
    css`
      align-items: center;
      border: ${Border.size.default} solid ${Border.color.default};
      border-radius: ${Border.radius.default};
      cursor: pointer;
      display: flex;
      flex-basis: 48%;
      flex-direction: row;
      margin: 0;
      padding: 15px;

      @media (max-width: 750px) {
        width: 100%;
        margin: 0 0 20px 0;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    `,
    props.isCompact &&
      css`
        flex: 5 1 0;

        &:not(:first-of-type) {
          flex: 3 1 0;
        }
      `,
    props.isDisabled &&
      css`
        cursor: default;
        opacity: 0.5;
      `,
    props.isOnly &&
      css`
        display: block;
        cursor: default;
        flex-basis: 100%;
        text-align: center;
      `,
    props.isSelected &&
      css`
        :not(:only-of-type) {
          border-color: ${Colors.heroine};
        }
      `,
  ]);

const descriptionCss = (props: Props) => css`
  text-align: ${props.isOnly ? 'center' : 'left'};
  margin-left: ${props.isOnly ? 0 : '20px'};
`;

const priceCss = css`
  font-size: 1.1em;
`;

const nameCss = css`
  color: ${Colors.deepSpace};
  font-size: 1.2em;
  font-weight: 600;
  margin-bottom: 10px;
  text-transform: capitalize;
`;

export const BillingOption = (props: Props) => {
  const {
    enticement,
    isDisabled = false,
    isMetered = false,
    isOnly = false,
    isSelected = false,
    name,
    onClick,
    price,
  } = props;

  return (
    <div
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      css={billingOptionCss(props)}
      onClick={isDisabled ? () => {} : onClick}
      onKeyDown={isDisabled ? () => {} : createKeyHandler(['space'], onClick)}
      role="radio"
      tabIndex={0}
    >
      {!isOnly && (
        <Radio
          aria-label={name}
          checked={isSelected}
          name="billing-option"
          onChange={() => {}} // Handled by wrapper div
          value={String(price)}
        />
      )}
      <div css={descriptionCss(props)}>
        <div css={nameCss}>
          {name} {enticement && <TagLabel color="info">{enticement}</TagLabel>}
        </div>

        <div css={priceCss}>
          {isMetered ? 'Starts at ' : ''}
          {price} per month
        </div>
      </div>
    </div>
  );
};
